
import React from "react";
import { ArrowDown, ArrowUp, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

interface TradingCardHeaderProps {
  sortField: string;
  sortDirection: "asc" | "desc" | null;
  onSort: (field: string) => void;
  // New props for drag and drop functionality
  columnOrder?: string[];
  onColumnReorder?: (fromIndex: number, toIndex: number) => void;
  hasHorizontalScroll?: boolean;
}

const TradingCardHeader: React.FC<TradingCardHeaderProps> = ({ 
  sortField, 
  sortDirection, 
  onSort,
  columnOrder,
  onColumnReorder,
  hasHorizontalScroll = false
}) => {
  const isMobile = useIsMobile();
  
  // Column definitions for drag and drop
  const columnDefinitions = [
    { id: "name", label: "Symbol", className: "flex-1 justify-start" },
    { id: "buyPrice", label: "Bid", className: "w-14 sm:w-20 text-right" },
    { id: "sellPrice", label: "Ask", className: "w-14 sm:w-20 text-right" }
  ];
  
  // Sorted column definitions based on order
  const sortedColumns = columnOrder 
    ? columnDefinitions.sort((a, b) => {
        const aIndex = columnOrder.indexOf(a.id);
        const bIndex = columnOrder.indexOf(b.id);
        return aIndex - bIndex;
      })
    : columnDefinitions;
  
  // Find Symbol column index in current order
  const symbolColumnIndex = sortedColumns.findIndex(col => col.id === "name");
  
  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (isMobile) return;
    e.dataTransfer.setData('text/plain', index.toString());
    e.currentTarget.classList.add('opacity-50');
  };
  
  const handleDragOver = (e: React.DragEvent) => {
    if (isMobile) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDragEnd = (e: React.DragEvent) => {
    if (isMobile) return;
    e.currentTarget.classList.remove('opacity-50');
  };
  
  const handleDrop = (e: React.DragEvent, toIndex: number) => {
    if (isMobile) return;
    e.preventDefault();
    const fromIndex = Number(e.dataTransfer.getData('text/plain'));
    
    if (fromIndex === toIndex) return;
    if (onColumnReorder) {
      onColumnReorder(fromIndex, toIndex);
    }
  };
  
  const renderSortIndicator = (field: string) => {
    if (sortField !== field || sortDirection === null) {
      return null;
    }
    
    return sortDirection === "asc" ? 
      <ArrowUp className="inline ml-1 text-theme-light" size={12} /> : 
      <ArrowDown className="inline ml-1 text-theme-light" size={12} />;
  };

  return (
    <div className={`sticky gap-5 top-0 z-30 flex items-center justify-between ${isMobile ? 'p-0.5' : 'p-2'} bg-muted/80 border-b border-border/20 rounded-t-md backdrop-blur-sm`}>
      {sortedColumns.map((column, index) => {
        const isSymbolColumn = column.id === "name";
        const shouldBeSticky = isSymbolColumn && hasHorizontalScroll;
        
        return (
          <Button
            key={column.id}
            variant="ghost"
            onClick={() => onSort(column.id)}
            className={`${column.className} font-medium hover:bg-transparent px-1 min-w-0 overflow-hidden text-theme-light h-7 relative ${
              shouldBeSticky ? "sticky-column" : ""
            }`}
            style={{ 
              fontSize: '0.9rem',
              ...(shouldBeSticky ? { 
                position: 'sticky', 
                left: 0, 
                zIndex: 40,
                backgroundColor: 'hsl(var(--background))',
                boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                borderRight: '1px solid hsl(var(--border) / 0.3)'
              } : {})
            }}
            draggable={!isMobile && !isSymbolColumn} // Prevent dragging the symbol column
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e)}
            onDragEnd={handleDragEnd}
            onDrop={(e) => handleDrop(e, index)}
          >
            <div className="flex items-center">
              {/* Only show drag handle on desktop and not for symbol column */}
              {!isMobile && !isSymbolColumn && (
                <GripVertical className="h-3 w-3 mr-1 opacity-50 drag-handle" />
              )}
              <span className="truncate">{column.label}</span>
            </div>
            {renderSortIndicator(column.id)}
          </Button>
        );
      })}
    </div>
  );
};

export default TradingCardHeader;
